import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchField from "../common/SearchField";
import { DashboardSummaryTabDto } from "../../types/dtos/dashboards";
import ManagerDashboardPersonDto from "../../types/dtos/dashboards/ManagerDashboardPersonDto";
import { AppraisalLevelClientTaskTypeDto } from "../../types/dtos/users/AppraisalLevelClientTaskTypeDto";
import LegacyReviewLink from "../common/LegacyReviewLink";
import LegacyTaskLink from "../common/LegacyTaskLink";
import PeopleCard from "./People/PeopleCard";
import ManagerDashboardEmptyState from "./ManagerDashboardEmptyState";
import { NextJourneyDto } from "../../types/journeys/NextJourney";
import { LatestIncompleteAnswerSetDto } from "../../types/dtos/dashboards/LatestIncompleteAnswerSetDto";

const LOADING_CARD_COUNT = 3;

const PEOPLE_PANEL_CLASS =
  "rounded-lg border border-gray-200 bg-white p-4";

interface PeopleWidgetProps {
  users: ManagerDashboardPersonDto[];
  nextJourneys: NextJourneyDto[];
  appraisalLevelTaskTypes: AppraisalLevelClientTaskTypeDto[];
  isLoading: boolean;
  isLegacyClient: boolean;
  clientHasTaskTypes: boolean;
  employeesHaveLegacyReviews: boolean;
  latestIncompleteAnswerSets: LatestIncompleteAnswerSetDto[];
  /** A function to call when the manager has selected to update a section for one of their people */
  onTriggerSectionUpdate(
    userId: number,
    summaryTab: DashboardSummaryTabDto,
    incompleteJourneyReference: string | null,
  ): void;
  onOpenUserHistory(userId: number): void;
  onLiveJourneyClick(userId: number): void | undefined;
  onUserTabChange?(
    userId: number,
    clientFormId: number,
    onSuccess: () => void,
    onError: () => void,
  ): void | undefined;
  onUpdateUser(user: ManagerDashboardPersonDto): void | undefined;
}

function PeopleWidget({
  users,
  nextJourneys,
  appraisalLevelTaskTypes,
  isLoading,
  isLegacyClient,
  clientHasTaskTypes,
  employeesHaveLegacyReviews,
  latestIncompleteAnswerSets,
  onTriggerSectionUpdate,
  onOpenUserHistory,
  onLiveJourneyClick,
  onUserTabChange = undefined,
  onUpdateUser,
}: PeopleWidgetProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [filteredUsers, setFilteredUsers] =
    useState<ManagerDashboardPersonDto[]>(users);

  const showLegacyLinks =
    isLegacyClient &&
    !isLoading &&
    (!clientHasTaskTypes || employeesHaveLegacyReviews);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
      return;
    }

    const term = searchTerm.toLowerCase();
    setFilteredUsers(
      users.filter((user) => user.fullName.toLowerCase().includes(term)),
    );
  }, [users, searchTerm]);

  return (
    <section aria-labelledby="manager-people-heading">
      <div className={PEOPLE_PANEL_CLASS}>
        <header className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3
            id="manager-people-heading"
            className="shrink-0 text-lg font-semibold leading-none text-gray-600 !mb-0"
          >
            {t("ManagerDashboard.People.Title", "Your People")}
          </h3>
          <SearchField
            id="manager-dashboard-search"
            onSearch={setSearchTerm}
            isLoading={isLoading}
            loaderColor="#9ca3af"
            includeSetWidth={false}
            outerContainerClassName="w-full sm:w-auto sm:shrink-0"
            className="w-full sm:max-w-xs lg:max-w-sm"
            inputFieldClassName="!bg-white border-gray-200 py-2 focus:border-primary focus:ring-2 focus:ring-primary/10 focus:outline-none"
            placeholderText={t(
              "ManagerDashboard.People.SearchPlaceholder",
              "Search for a user...",
            )}
          />
        </div>
        {showLegacyLinks && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
            <span>
              {t("ManagerDashboard.People.HistoricData", "Historic data:")}
            </span>
            {!clientHasTaskTypes && <LegacyTaskLink />}
            {employeesHaveLegacyReviews && <LegacyReviewLink />}
          </div>
        )}
        </header>

        <div aria-busy={isLoading}>
        {isLoading &&
          Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
            <PeopleCard
              key={`people-loading-${index}`}
              user={null}
              appraisalLevelTaskTypes={[]}
              isLegacyClient={isLegacyClient}
              onTriggerSectionUpdate={onTriggerSectionUpdate}
              onLiveJourneyClick={onLiveJourneyClick}
              onUpdateUser={onUpdateUser}
              nextJourney={null}
              latestIncompleteAnswerSet={null}
            />
          ))}

        {!isLoading && filteredUsers.length === 0 && (
          <ManagerDashboardEmptyState
            optionalText={
              searchTerm
                ? t(
                    "ManagerDashboard.People.NoSearchResults",
                    "There are no people that match your search. Try a different name.",
                  )
                : undefined
            }
          />
        )}

        {!isLoading &&
          filteredUsers.length > 0 &&
          filteredUsers.map((user) => {
            const nextJourney =
              nextJourneys.find((nj) => nj.employeeId === user.userId) ?? null;
            const latestIncompleteAnswerSet =
              latestIncompleteAnswerSets.find(
                (lis) => lis.employeeId === user.userId,
              ) ?? null;

            return (
              <PeopleCard
                key={`user_${user.userId}`}
                user={user}
                nextJourney={nextJourney}
                latestIncompleteAnswerSet={latestIncompleteAnswerSet}
                appraisalLevelTaskTypes={appraisalLevelTaskTypes}
                isLegacyClient={isLegacyClient}
                onTriggerSectionUpdate={onTriggerSectionUpdate}
                onUserTabChange={onUserTabChange}
                onOpenUserHistory={
                  user.hasHistoricalFormData ? onOpenUserHistory : undefined
                }
                onLiveJourneyClick={onLiveJourneyClick}
                onUpdateUser={onUpdateUser}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default PeopleWidget;
